import {
  Interval,
  Store,
  SubscriptionType,
  Timestamp,
  UserSubscription,
} from '@griply/types';

export enum SubscriptionCardKind {
  Lifetime = 'lifetime',
  Subscription = 'subscription',
  Promotional = 'promotional',
}

export enum SubscriptionStatus {
  Active = 'active',
  Trial = 'trial',
  Canceled = 'canceled',
  Purchased = 'purchased',
  TrialExpired = 'trial-expired',
}

export type SubscriptionCard = {
  kind: SubscriptionCardKind;
  status: SubscriptionStatus | null;
  expiresAt: Timestamp | null;
  purchasedAt: Timestamp;
  stripeCustomerId?: string;
  managementUrl?: string;
  isApple: boolean;
};

const mostRecent = (
  subscriptions: UserSubscription[],
  predicate: (subscription: UserSubscription) => boolean,
): UserSubscription | null =>
  subscriptions.reduce<UserSubscription | null>((best, subscription) => {
    if (!predicate(subscription)) {
      return best;
    }
    if (!best) {
      return subscription;
    }
    return subscription.purchasedAt.getTime() > best.purchasedAt.getTime()
      ? subscription
      : best;
  }, null);

const getLifetimeStatus = (
  subscription: UserSubscription,
  today: Date,
): SubscriptionStatus | null => {
  if (subscription.expiresAt === null) {
    const purchased =
      subscription.type === SubscriptionType.Paid ||
      subscription.type === SubscriptionType.Premium;
    return purchased ? SubscriptionStatus.Purchased : null;
  }

  return today.getTime() > subscription.expiresAt.getTime()
    ? SubscriptionStatus.TrialExpired
    : SubscriptionStatus.Trial;
};

const getSubscriptionStatus = (
  subscription: UserSubscription,
): SubscriptionStatus | null => {
  if (subscription.unsubscribedAt) {
    return SubscriptionStatus.Canceled;
  }
  if (subscription.type === SubscriptionType.Trial) {
    return SubscriptionStatus.Trial;
  }
  if (subscription.type === SubscriptionType.Paid) {
    return SubscriptionStatus.Active;
  }
  return null;
};

const toCard = (
  kind: SubscriptionCardKind,
  status: SubscriptionStatus | null,
  subscription: UserSubscription,
): SubscriptionCard => ({
  kind,
  status,
  expiresAt: subscription.expiresAt,
  purchasedAt: subscription.purchasedAt,
  stripeCustomerId: subscription.stripeCustomerId,
  managementUrl: subscription.managementUrl,
  isApple: !subscription.stripeCustomerId && !!subscription.managementUrl,
});

export const getSubscriptionCards = (
  subscriptions: UserSubscription[] | undefined,
  today: Date,
): SubscriptionCard[] => {
  if (!subscriptions?.length) {
    return [];
  }

  const lifetime = mostRecent(
    subscriptions,
    ({ interval }) => interval === Interval.Lifetime,
  );

  const subscription = mostRecent(
    subscriptions,
    ({ interval, store }) =>
      interval !== Interval.Lifetime && store !== Store.Promotional,
  );

  const promotional = mostRecent(
    subscriptions,
    ({ store, productId }) =>
      store === Store.Promotional && !productId?.includes('custom'),
  );

  const cards: SubscriptionCard[] = [];

  if (lifetime) {
    cards.push(
      toCard(
        SubscriptionCardKind.Lifetime,
        getLifetimeStatus(lifetime, today),
        lifetime,
      ),
    );
  }

  if (subscription) {
    cards.push(
      toCard(
        SubscriptionCardKind.Subscription,
        getSubscriptionStatus(subscription),
        subscription,
      ),
    );
  }

  if (promotional) {
    cards.push(
      toCard(
        SubscriptionCardKind.Promotional,
        SubscriptionStatus.Active,
        promotional,
      ),
    );
  }

  return cards;
};
